import { useRef, useState } from "react";
import { Upload, Camera, Check, X, FileText, AlertCircle } from "lucide-react";
import api from "@/lib/api";

function DocUploader({ label, value, onChange, testId }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch { /* */ }
    setUploading(false);
  };

  const handleCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      const video = document.createElement("video");
      video.srcObject = stream;
      await video.play();
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      stream.getTracks().forEach((t) => t.stop());
      canvas.toBlob(async (blob) => {
        if (blob) await handleFile(new File([blob], "capture.jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.8);
    } catch { inputRef.current?.click(); }
  };

  return (
    <div className="crm-card overflow-hidden" data-testid={testId}>
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--crm-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "var(--crm-accent-subtle)", color: "var(--crm-accent)" }}>
            <FileText className="w-4 h-4" />
          </div>
          <span className="text-sm font-bold" style={{ color: "var(--crm-text)" }}>{label}</span>
        </div>
        {value ? (
          <span className="crm-badge crm-badge-green text-xs font-semibold"><Check className="w-3 h-3" /> Enviado</span>
        ) : (
          <span className="crm-badge crm-badge-orange text-xs font-semibold"><AlertCircle className="w-3 h-3" /> Obrigatório</span>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {value ? (
          <div className="relative group">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${value}`} alt={label} className="w-full h-48 object-cover rounded-xl" />
            <button onClick={() => onChange(null)}
              className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center bg-red-500 text-white shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="rounded-xl p-8 text-center" style={{ border: "2px dashed var(--crm-border)", background: "var(--crm-input-bg)" }}>
            <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--crm-text-dim)" }} />
            <p className="text-sm font-medium mb-4" style={{ color: "var(--crm-text-muted)" }}>Arraste uma imagem ou use os botões abaixo</p>
            <div className="flex gap-3 justify-center">
              <button onClick={() => inputRef.current?.click()} disabled={uploading} className="crm-btn-secondary text-sm py-3 px-5">
                <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Upload Arquivo"}
              </button>
              <button onClick={handleCapture} disabled={uploading} className="crm-btn-primary text-sm py-3 px-5">
                <Camera className="w-4 h-4" /> Usar Câmera
              </button>
            </div>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
    </div>
  );
}

export default function StepDocuments({ documents, setDocuments }) {
  return (
    <div data-testid="step-documents">
      <h2 className="crm-heading mb-2">Documentos</h2>
      <p className="text-sm mb-8" style={{ color: "var(--crm-text-muted)" }}>
        Envie fotos do documento de identificação. Frente e verso são obrigatórios.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <DocUploader label="Documento (Frente)" value={documents.id_front} onChange={(url) => setDocuments({ ...documents, id_front: url })} testId="wizard-doc-front" />
        <DocUploader label="Documento (Verso)" value={documents.id_back} onChange={(url) => setDocuments({ ...documents, id_back: url })} testId="wizard-doc-back" />
      </div>
    </div>
  );
}
